import {
  faAngleLeft,
  faArrowRight,
  faCheck,
  faLinkSlash,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { Alert, Button, Modal, TagList, TaskBar, Value, ValueList } from '.';
import useSchemaSync from '../hooks/schema-sync';
import { List, MoveListsChange, MoveListsResult } from '../types';
import './ListScopeModal.scss';

const SCOPE_PATTERN = /^[a-z0-9_-]{1,50}$/;

type ListScopeModalProps = {
  list: Pick<List, 'id' | 'pathName' | 'scope'>;
  show?: boolean;
  onClose?: () => void;

  // Called once a move has been applied
  onMoved?: (result: MoveListsResult) => void;
};

const ACTION_LABELS: Record<MoveListsChange['action'], string> = {
  move: 'Move',
  assign: 'Assign',
  release: 'Release',
  'no-change': 'No change',
  error: 'Error',
};

/**
 * Move a list to another schema sync scope, assign it to one, or release it
 * from its scope. A dry run shows what will change before it's applied
 */
export function ListScopeModal({
  list,
  show,
  onClose,
  onMoved,
}: ListScopeModalProps): ReactElement {
  const { pending, error, resetError, moveLists } = useSchemaSync();

  const [scope, setScope] = useState('');
  const [plan, setPlan] = useState<MoveListsResult | null>(null);

  const normalisedScope = scope.trim().toLowerCase();
  const scopeValid = SCOPE_PATTERN.test(normalisedScope);
  const managed = !!list.scope;

  // Start again each time the modal opens
  useEffect(() => {
    if (show) {
      setScope('');
      setPlan(null);
      resetError();
    }
  }, [show, resetError]);

  const preview = useCallback(
    async (target: string | null) => {
      const result = await moveLists({ lists: [list.id] }, target, {
        dryRun: true,
      });

      if (result) {
        setPlan(result);
      }
    },
    [moveLists, list.id]
  );

  const handleApply = useCallback(async () => {
    if (!plan) {
      return;
    }

    const result = await moveLists({ lists: [list.id] }, plan.scope);

    if (!result) {
      return;
    }

    if (result.applied) {
      onMoved?.(result);
      onClose?.();
    } else {
      // Something changed since the dry run, so show why it was refused
      setPlan(result);
    }
  }, [moveLists, list.id, plan, onMoved, onClose]);

  const change = plan?.changes[0];

  // What the user asked for, whatever the plan says about it
  const intent: 'move' | 'assign' | 'release' =
    plan?.scope === null ? 'release' : managed ? 'move' : 'assign';
  const canApply =
    !!plan &&
    !plan.blockedBy &&
    !!change &&
    change.action !== 'no-change' &&
    change.action !== 'error';

  return (
    <Modal
      className="list-scope-modal"
      show={show}
      closable
      dismissable
      onClose={onClose}
      small
    >
      <h2>{managed ? 'Change scope' : 'Assign to a scope'}</h2>
      <Alert intent="danger" show={!!error}>
        <p>{error?.message}</p>
      </Alert>
      {!plan ? (
        <>
          <p>
            {managed ? (
              <>
                This list is managed by the schema sync scope{' '}
                <code>{list.scope}</code>. Move it to another scope, or release
                it so that no scope manages it.
              </>
            ) : (
              <>
                No schema sync scope manages this list. Assigning it to a scope
                lets that scope's document sync it (and prune it).
              </>
            )}{' '}
            <a
              href="/docs/schema-sync#moving-lists-between-scopes"
              target="_blank"
              rel="noreferrer"
            >
              Moving lists between scopes
            </a>
          </p>
          <ValueList fullWidth>
            <Value
              label={managed ? 'New scope' : 'Scope'}
              htmlFor="list-scope-input"
              value={
                <>
                  <input
                    type="text"
                    id="list-scope-input"
                    value={scope}
                    placeholder="e.g. recipe-app"
                    onChange={e => setScope(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && scopeValid) {
                        preview(normalisedScope);
                      }
                    }}
                    disabled={pending}
                  />
                  <span className="extra list-scope-modal-hint">
                    1-50 letters, numbers, hyphens and underscores. The list is
                    tagged with its scope.
                  </span>
                </>
              }
            />
          </ValueList>
          <TaskBar
            left={
              managed ? (
                <Button
                  intent="warning"
                  icon={faLinkSlash}
                  label="Release from scope"
                  onClick={() => preview(null)}
                  disabled={pending}
                  secondary
                />
              ) : undefined
            }
            right={
              <>
                <Button
                  icon={faTimes}
                  label="Cancel"
                  onClick={onClose}
                  secondary
                />
                <Button
                  intent="info"
                  icon={faArrowRight}
                  label="Continue"
                  onClick={() => preview(normalisedScope)}
                  disabled={pending || !scopeValid}
                />
              </>
            }
            background={false}
          />
        </>
      ) : (
        <>
          <p>
            {
              {
                release:
                  'Releasing the list from its scope will make these changes:',
                move: `Moving the list to scope "${plan.scope}" will make these changes:`,
                assign: `Assigning the list to scope "${plan.scope}" will make these changes:`,
              }[intent]
            }
          </p>
          {change && (
            <ValueList
              values={{
                Action: ACTION_LABELS[change.action],
                'From scope': change.from ?? (
                  <span className="extra">No scope</span>
                ),
                'To scope': change.to ?? (
                  <span className="extra">No scope</span>
                ),
                ...(change.indexes !== undefined && {
                  'Managed indexes': change.indexes,
                }),
                ...(change.fields?.tags && {
                  Tags: (
                    <span className="list-scope-modal-tags">
                      {change.fields.tags.from.length > 0 ? (
                        <TagList
                          tags={change.fields.tags.from}
                          linked={false}
                        />
                      ) : (
                        <span className="extra">No tags</span>
                      )}
                      <span className="extra">&rarr;</span>
                      {change.fields.tags.to.length > 0 ? (
                        <TagList tags={change.fields.tags.to} linked={false} />
                      ) : (
                        <span className="extra">No tags</span>
                      )}
                    </span>
                  ),
                }),
              }}
            />
          )}
          {[...plan.warnings, ...(change?.warnings ?? [])].map(warning => (
            <Alert key={warning} intent="warning" show>
              <p>{warning}</p>
            </Alert>
          ))}
          {change?.errors?.map(changeError => (
            <Alert key={changeError.message} intent="danger" show>
              <p>{changeError.message}</p>
            </Alert>
          ))}
          <TaskBar
            left={
              <Button
                icon={faAngleLeft}
                label="Back"
                onClick={() => setPlan(null)}
                disabled={pending}
                secondary
              />
            }
            right={
              <>
                <Button
                  icon={faTimes}
                  label="Cancel"
                  onClick={onClose}
                  secondary
                />
                <Button
                  intent={intent === 'release' ? 'warning' : 'info'}
                  icon={faCheck}
                  label={ACTION_LABELS[intent]}
                  onClick={handleApply}
                  disabled={pending || !canApply}
                />
              </>
            }
            background={false}
          />
        </>
      )}
    </Modal>
  );
}
